import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, Zap, Loader2 } from "lucide-react";
import { formatDate } from "@/lib/constants";

const CAT = ["Equity", "Debt", "Hybrid", "Money Market"];

export default function BenchmarksAdmin() {
  const [benches, setBenches] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tickers, setTickers] = useState([]);
  const [syncingId, setSyncingId] = useState(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [f, setF] = useState(emptyForm());

  function emptyForm() {
    return {
      name: "", mapped_category: "", mapped_sub_category: "",
      trailing_returns: { return_1yr_pct: "", return_3yr_pct: "", return_5yr_pct: "" },
      data_as_of_date: "",
      ticker_symbol: "",
    };
  }

  const load = async () => {
    const { data } = await api.get("/index-benchmarks");
    setBenches(data);
  };

  const loadTickers = async () => {
    try {
      const { data } = await api.get("/index-benchmarks/suggested-tickers");
      setTickers(data.tickers || []);
    } catch { /* not critical — the input still accepts free-text */ }
  };

  useEffect(() => { load(); loadTickers(); }, []);

  const open = (b) => {
    setEditing(b);
    setF(b ? {
      name: b.name || "",
      mapped_category: b.mapped_category || "",
      mapped_sub_category: b.mapped_sub_category || "",
      trailing_returns: {
        return_1yr_pct: b.trailing_returns?.return_1yr_pct ?? "",
        return_3yr_pct: b.trailing_returns?.return_3yr_pct ?? "",
        return_5yr_pct: b.trailing_returns?.return_5yr_pct ?? "",
      },
      data_as_of_date: b.data_as_of_date || "",
      ticker_symbol: b.ticker_symbol || "",
    } : emptyForm());
    setDlg(true);
  };

  const pickTicker = (t) => {
    setF((prev) => ({
      ...prev,
      ticker_symbol: t.ticker,
      name: prev.name || t.label,
      mapped_category: prev.mapped_category || t.category,
      mapped_sub_category: prev.mapped_sub_category || t.sub_category,
    }));
  };

  const previewNow = async () => {
    const ticker = f.ticker_symbol?.trim();
    if (!ticker) { toast.error("Enter or pick a ticker first"); return; }
    setPreviewBusy(true);
    try {
      const { data } = await api.get(`/index-benchmarks/preview`, { params: { ticker } });
      setF((prev) => ({
        ...prev,
        trailing_returns: {
          return_1yr_pct: data.trailing_returns?.return_1yr_pct ?? "",
          return_3yr_pct: data.trailing_returns?.return_3yr_pct ?? "",
          return_5yr_pct: data.trailing_returns?.return_5yr_pct ?? "",
        },
        data_as_of_date: data.latest_close_date || prev.data_as_of_date,
      }));
      toast.success(`Fetched from Yahoo · close ${data.latest_close} on ${data.latest_close_date}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewBusy(false);
    }
  };

  const save = async () => {
    if (!f.name) { toast.error("Name is required"); return; }
    const payload = {
      ...f,
      trailing_returns: {
        return_1yr_pct: f.trailing_returns.return_1yr_pct === "" ? null : Number(f.trailing_returns.return_1yr_pct),
        return_3yr_pct: f.trailing_returns.return_3yr_pct === "" ? null : Number(f.trailing_returns.return_3yr_pct),
        return_5yr_pct: f.trailing_returns.return_5yr_pct === "" ? null : Number(f.trailing_returns.return_5yr_pct),
      },
    };
    try {
      if (editing) await api.patch(`/index-benchmarks/${editing.id}`, payload);
      else await api.post("/index-benchmarks", payload);
      toast.success("Saved");
      setDlg(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.name}"?`)) return;
    try {
      await api.delete(`/index-benchmarks/${b.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  const syncOne = async (b) => {
    if (!b.ticker_symbol) { toast.error("Set a ticker on this benchmark first"); return; }
    setSyncingId(b.id);
    try {
      const { data } = await api.post(`/index-benchmarks/${b.id}/sync`);
      toast.success(`Synced ${b.name} · 1Y ${fmtPct(data.trailing_returns?.return_1yr_pct)}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingId(null);
    }
  };

  const syncAll = async () => {
    const withTicker = benches.filter((b) => b.ticker_symbol);
    if (withTicker.length === 0) {
      toast.error("Add a ticker to at least one benchmark first");
      return;
    }
    setBulkSyncing(true);
    try {
      const { data } = await api.post("/index-benchmarks/sync-all");
      if (data.failed === 0) {
        toast.success(`Synced all ${data.succeeded} benchmarks from Yahoo Finance`);
      } else {
        toast.warning(`${data.succeeded} synced, ${data.failed} failed`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setBulkSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline text-primary">Admin · Reference data</p>
          <h1 className="font-dashboard text-3xl font-bold tracking-tight">Index Benchmarks</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Live-sync trailing 1Y/3Y/5Y returns from Yahoo Finance for the major NSE / BSE indices — or enter numbers manually from the latest AMFI/NSE factsheet.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={syncAll}
            disabled={bulkSyncing}
            variant="outline"
            className="gap-1 border-accent/40 text-accent hover:bg-accent/10"
            data-testid="sync-all-benchmarks-btn"
          >
            {bulkSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync all from Yahoo
          </Button>
          <Button onClick={() => open(null)} className="gap-1" data-testid="add-benchmark-btn">
            <Plus className="h-4 w-4" /> New benchmark
          </Button>
        </div>
      </div>

      <Card className="border-border bg-white p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Ticker</th>
              <th className="px-4 py-3">Maps to</th>
              <th className="px-4 py-3 text-right">1Y</th>
              <th className="px-4 py-3 text-right">3Y</th>
              <th className="px-4 py-3 text-right">5Y</th>
              <th className="px-4 py-3">As-of</th>
              <th className="px-4 py-3">Last synced</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {benches.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                No benchmarks yet. Click <span className="font-semibold text-foreground">New benchmark</span>, pick a ticker (e.g. ^NSEI for Nifty 50), and hit Preview to auto-fill returns.
              </td></tr>
            )}
            {benches.map((b) => (
              <tr key={b.id} data-testid={`benchmark-row-${b.id}`}>
                <td className="px-4 py-3 font-medium">{b.name}</td>
                <td className="px-4 py-3">
                  {b.ticker_symbol
                    ? <code className="rounded bg-secondary/60 px-1.5 py-0.5 text-[11px]">{b.ticker_symbol}</code>
                    : <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {b.mapped_category || "—"}
                  {b.mapped_sub_category ? ` · ${b.mapped_sub_category}` : ""}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtPct(b.trailing_returns?.return_1yr_pct)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtPct(b.trailing_returns?.return_3yr_pct)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtPct(b.trailing_returns?.return_5yr_pct)}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(b.data_as_of_date) || "—"}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground" data-testid={`bench-last-synced-${b.id}`}>
                  {b.last_synced_at ? formatDate(b.last_synced_at) : "Never"}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => syncOne(b)}
                    disabled={!b.ticker_symbol || syncingId === b.id}
                    title={b.ticker_symbol ? "Sync from Yahoo Finance" : "Set a ticker first"}
                    data-testid={`sync-benchmark-${b.id}`}
                  >
                    {syncingId === b.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => open(b)} data-testid={`edit-benchmark-${b.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(b)} data-testid={`del-benchmark-${b.id}`}><Trash2 className="h-3 w-3" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit benchmark" : "New benchmark"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Name</Label>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Nifty 50" data-testid="bench-name-input" />
            </div>

            <div className="rounded-md border border-accent/30 bg-accent/5 p-3">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-accent">
                <Zap className="h-3 w-3" /> Live data from Yahoo Finance (price return)
              </div>
              <div className="grid gap-2 md:grid-cols-[1fr_auto]">
                <Input
                  value={f.ticker_symbol}
                  onChange={(e) => setF({ ...f, ticker_symbol: e.target.value })}
                  placeholder="^NSEI  ·  ^NSEBANK  ·  ^CNXIT …"
                  data-testid="bench-ticker-input"
                />
                <Button
                  type="button"
                  onClick={previewNow}
                  disabled={!f.ticker_symbol?.trim() || previewBusy}
                  variant="outline"
                  className="gap-1 border-accent/40 text-accent hover:bg-accent/10"
                  data-testid="bench-preview-btn"
                >
                  {previewBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Preview
                </Button>
              </div>
              {tickers.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid="ticker-suggestions">
                  {tickers.map((t) => (
                    <button
                      type="button"
                      key={t.ticker}
                      onClick={() => pickTicker(t)}
                      className={`rounded-full px-2 py-0.5 text-[11px] transition ${
                        f.ticker_symbol === t.ticker
                          ? "bg-primary text-primary-foreground"
                          : "bg-white text-muted-foreground hover:bg-accent/10 hover:text-accent"
                      }`}
                      data-testid={`ticker-chip-${t.ticker}`}
                    >
                      {t.label} <span className="opacity-60">· {t.ticker}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>Category</Label>
                <select
                  className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={f.mapped_category}
                  onChange={(e) => setF({ ...f, mapped_category: e.target.value })}
                  data-testid="bench-category-input"
                >
                  <option value="">—</option>
                  {CAT.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Sub-category</Label>
                <Input value={f.mapped_sub_category} onChange={(e) => setF({ ...f, mapped_sub_category: e.target.value })} placeholder="e.g. Large Cap" data-testid="bench-subcat-input" />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div><Label className="text-xs">1Y return %</Label><Input type="number" step="0.01" value={f.trailing_returns.return_1yr_pct} onChange={(e) => setF({ ...f, trailing_returns: { ...f.trailing_returns, return_1yr_pct: e.target.value } })} data-testid="bench-r1y" /></div>
              <div><Label className="text-xs">3Y return %</Label><Input type="number" step="0.01" value={f.trailing_returns.return_3yr_pct} onChange={(e) => setF({ ...f, trailing_returns: { ...f.trailing_returns, return_3yr_pct: e.target.value } })} data-testid="bench-r3y" /></div>
              <div><Label className="text-xs">5Y return %</Label><Input type="number" step="0.01" value={f.trailing_returns.return_5yr_pct} onChange={(e) => setF({ ...f, trailing_returns: { ...f.trailing_returns, return_5yr_pct: e.target.value } })} data-testid="bench-r5y" /></div>
            </div>
            <div>
              <Label>Data as-of</Label>
              <Input type="date" value={f.data_as_of_date?.slice(0, 10) || ""} onChange={(e) => setF({ ...f, data_as_of_date: e.target.value })} data-testid="bench-asof" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} data-testid="bench-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const fmtPct = (v) => (v == null || v === "" ? "—" : `${Number(v).toFixed(2)}%`);
